// service-worker-update.service.ts
import { ApplicationRef, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate } from '@angular/service-worker';
import { concat, first, interval } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerUpdateService {
  private readonly UPDATE_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

  constructor(
    private appRef: ApplicationRef,
    private updates: SwUpdate,
    private snackBar: MatSnackBar
  ) {
    if (!this.updates.isEnabled) {
      console.log('Service worker updates are disabled.');
      return;
    }

    // Initial check for updates on app startup
    this.checkForUpdates();

    // Set up periodic update checks after the app stabilizes
    const appIsStable$ = this.appRef.isStable.pipe(
      first(isStable => isStable === true)
    );
    const periodicUpdateCheck$ = interval(this.UPDATE_CHECK_INTERVAL);
    const updateCheckSequence$ = concat(appIsStable$, periodicUpdateCheck$);

    updateCheckSequence$.subscribe(() => {
      this.checkForUpdates();
    });

    // Optional: Listen for version updates triggered by the service worker
    this.updates.versionUpdates.subscribe(evt => {
      console.debug('Version update detected:', evt);
      if (evt.type === 'VERSION_READY') {
        this.promptUser();
      }
    });
  }

  /**
   * Checks for available updates.
   */
  private async checkForUpdates() {
    try {
      const updateFound = await this.updates.checkForUpdate();
      if (updateFound) {
        console.debug('A new version is available.');
        this.promptUser();
      } else {
        console.debug('Already on the latest version.');
      }
    } catch (err) {
      console.error('Failed to check for updates:', err);
    }
  }

  /**
   * Prompts the user to update the application.
   */
  private promptUser() {
    const snackBarRef = this.snackBar.open(
      '새로운 버전으로 업데이트할 수 있습니다.', // "A new version is available for update."
      '업데이트', // "Update"
      {
        duration: 0, // Snackbar remains open until dismissed
        horizontalPosition: 'center',
        verticalPosition: 'top',
      }
    );

    snackBarRef.onAction().subscribe(() => {
      this.activateUpdate();
    });
  }

  /**
   * Activates the update and reloads the page.
   */
  private activateUpdate() {
    this.updates
      .activateUpdate()
      .then(() => {
        console.log('Update activated. Reloading page...');
        document.location.reload();
      })
      .catch(err => {
        console.error('Failed to activate update:', err);
      });
  }
}
